import { Duration } from "luxon";

import { createLogger } from "@/logger";
import { syncCurrentRoutes } from "@/models/currentRoutes/currentRoutesSync";
import { browserPollerClock, startPolling } from "@/models/sync/eventLogSync";
import type { SyncContext } from "@/models/sync/syncTargets";

const log = createLogger("appsync");

/**
 * How often a visible tab syncs every driver's current routes: each tick resumes an open walk or starts the next one,
 * full or a delta as the server decides. A stop departing can take up to this long to show on the table.
 */
export const CURRENT_ROUTES_POLL_INTERVAL_MS = Duration.fromObject({ minutes: 1 }).toMillis();

/** Spread on the interval so tabs that booted together do not poll in lockstep. */
export const CURRENT_ROUTES_POLL_JITTER_MS = Duration.fromObject({ seconds: 5 }).toMillis();

/** The next tick's delay: the interval plus or minus the jitter. */
export function nextCurrentRoutesPollDelayMs(random: () => number = Math.random): number {
  return CURRENT_ROUTES_POLL_INTERVAL_MS + Math.round((random() * 2 - 1) * CURRENT_ROUTES_POLL_JITTER_MS);
}

/**
 * Syncs the workspace's current routes on a timer for as long as the workspace is open.
 *
 * Polled rather than pushed: routes arrive through ELD ingestion, not AppSync. A hidden tab lets its ticks pass and
 * syncs once when shown again; a walk already in flight runs to its end either way.
 *
 * @returns Stops the poller; safe to call more than once.
 */
export function startCurrentRoutesPollingInBrowser(ctx: SyncContext): () => void {
  const syncLogged = async () => {
    try {
      await syncCurrentRoutes(ctx);
    } catch (error) {
      log.warn("[sync] Current routes poll failed; the next tick retries", { error });
    }
  };
  return startPolling(syncLogged, browserPollerClock(nextCurrentRoutesPollDelayMs));
}
